/**
 * Idle-time route prefetching.
 *
 * Triggered after first paint on the home page to warm the lazy-loaded
 * chunks for the most-trafficked secondary routes. Each importer is the
 * SAME function used by `React.lazy(...)` in App.tsx so the browser will
 * cache the module and subsequent navigations resolve synchronously.
 */
type ChunkImporter = () => Promise<unknown>;

type PrefetchEntry = { path: string; importer: ChunkImporter };

/* ------------------------------------------------------------------ *
 * Connection-aware gating + concurrency limiter
 *
 * Mobile devices on slow / metered networks pay a real LCP/TBT tax
 * when we eagerly fan out modulepreload + image-shim requests. The
 * helpers below let every prefetch path opt into:
 *
 *   1. shouldPrefetch() — refuses to prefetch on Save-Data, 2g/slow-2g,
 *      or links with downlink < 1.5 Mbps. Also bails when the device
 *      reports `connection.saveData === true` even on faster radios.
 *   2. enqueuePrefetch() — caps in-flight prefetch imports at
 *      MAX_CONCURRENT (2) so we never saturate the socket pool while
 *      the LCP image / hero JS is still arriving.
 * ------------------------------------------------------------------ */

type NetInfo = {
  saveData?: boolean;
  effectiveType?: '2g' | '3g' | '4g' | 'slow-2g' | string;
  downlink?: number;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
};

const getConnection = (): NetInfo | undefined => {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & { connection?: NetInfo }).connection;
};

/**
 * Returns false on slow / metered networks. When this returns false
 * we skip *speculative* prefetches entirely; explicit user navigation
 * is unaffected (React.lazy still imports on demand).
 */
export function shouldPrefetch(): boolean {
  const c = getConnection();
  if (!c) return true; // No NetInfo support → assume OK (desktop, Safari).
  if (c.saveData) return false;
  const et = c.effectiveType;
  if (et === 'slow-2g' || et === '2g' || et === '3g') return false;
  if (typeof c.downlink === 'number' && c.downlink > 0 && c.downlink < 1.5) {
    return false;
  }
  return true;
}

const MAX_CONCURRENT = 2;
let inflight = 0;
const queue: Array<() => void> = [];

const drain = () => {
  while (inflight < MAX_CONCURRENT && queue.length > 0) {
    const next = queue.shift();
    if (next) next();
  }
};

/**
 * Schedule a prefetch task through the concurrency limiter. The task
 * MUST return a promise so we know when its socket is freed. If the
 * network looks slow/metered the task is dropped (returns void) — the
 * caller's idempotency guard (warmedPaths) will let it retry once the
 * connection improves and a `change` event fires.
 */
const enqueuePrefetch = (task: () => Promise<unknown>): void => {
  if (!shouldPrefetch()) return;
  const run = () => {
    inflight += 1;
    task().finally(() => {
      inflight -= 1;
      drain();
    });
  };
  if (inflight < MAX_CONCURRENT) {
    run();
  } else {
    queue.push(run);
  }
};

// React to network changes: when the user moves from slow → fast we
// re-warm whatever the home page wanted to prefetch initially. We do
// NOT auto-flush the queue downward — pending tasks just stay parked
// until a slot frees, which is the correct behaviour on a degrading
// link too (older requests will time out naturally).
if (typeof window !== 'undefined') {
  const c = getConnection();
  c?.addEventListener?.('change', () => {
    if (shouldPrefetch()) drain();
  });
}

/**
 * Path patterns whose chunks we warm at idle time. The `path` field is used
 * by the route-fallback instrumentation to flag whether a Suspense wait
 * happened on a path we *expected* to be cached.
 *
 * NOTE: `/exhibits` is intentionally NOT in this list. The Exhibits page
 * transitively imports `@/data/museum-data`, which compiles to a ~214 KB
 * gzipped chunk. Idle-warming it from every home visit pulled that chunk
 * onto the mobile critical path (visible in Lighthouse as the largest
 * post-LCP resource on `/`). Hover / visibility prefetch via
 * `<PrefetchLink>` (see PREFETCH_IMPORTERS below) still warms the chunk
 * the moment the user shows intent, so first-click navigation feels
 * synchronous without taxing the home-page bandwidth budget.
 */
const TOP_ROUTE_ENTRIES: PrefetchEntry[] = [
  { path: '/visit', importer: () => import('@/pages/Visit') },
  { path: '/contact', importer: () => import('@/pages/Contact') },
  { path: '/document-search', importer: () => import('@/pages/DocumentSearch') },
  { path: '/historical-gallery', importer: () => import('@/pages/HistoricalGalleryPage') },
];

const PREFETCHED_PATHS = new Set(TOP_ROUTE_ENTRIES.map((e) => e.path));

// Path → importer map used for on-demand prefetching (hover/visibility).
// Extend this when adding new prefetchable destinations; entries here may
// or may not also live in TOP_ROUTE_ENTRIES (which only controls idle warmup).
const PREFETCH_IMPORTERS: Record<string, ChunkImporter> = {
  '/exhibits': () => import('@/pages/Exhibits'),
  '/visit': () => import('@/pages/Visit'),
  '/contact': () => import('@/pages/Contact'),
  '/document-search': () => import('@/pages/DocumentSearch'),
  '/historical-gallery': () => import('@/pages/HistoricalGalleryPage'),
  // High-traffic header destinations. Hover/visibility prefetch via
  // <PrefetchLink> warms these on intent so first navigation feels
  // synchronous, without paying the idle-warmup bandwidth tax on mobile
  // (intentionally NOT added to TOP_ROUTE_ENTRIES).
  '/collections': () => import('@/pages/Collections'),
  '/museum': () => import('@/pages/Museum'),
  '/on-display': () => import('@/pages/OnDisplay'),
  '/texas-rangers': () => import('@/pages/TexasRangers'),
  '/texas-ranger-hall': () => import('@/pages/TexasRangerHall'),
  '/firearms-collection': () => import('@/pages/FirearmsCollection'),
  '/heritage': () => import('@/pages/Heritage'),
  '/stewardship': () => import('@/pages/Stewardship'),
  '/wildlife-safari': () => import('@/pages/WildlifeSafari'),
  '/famous-visitors': () => import('@/pages/FamousVisitors'),
  '/explore': () => import('@/pages/Explore'),
  // Gallery routes — every entry below resolves to the same shared
  // `galleries` Rollup chunk (see vite.config.ts manualChunks). Importing
  // any one of them warms the chunk for every other gallery destination,
  // so hovering/clicking a single gallery nav link makes subsequent
  // gallery navigations resolve synchronously.
  '/longhorn-gallery': () => import('@/pages/LonghornGallery'),
  '/ranch-found-photographs': () => import('@/pages/RanchFoundPhotographs'),
  '/photo-search': () => import('@/pages/PhotoSearch'),
  '/galleries': () => import('@/pages/GalleryPage'),
};

/**
 * Heritage routes all resolve through the shared `HeritagePageRenderer`
 * chunk (see App.tsx → `HeritagePageRendererLazy`). Warming the renderer
 * via any one of these paths makes every subsequent heritage navigation
 * resolve synchronously, so hovering one heritage nav link primes all of
 * them.
 */
const HERITAGE_SLUGS = [
  '/wildlife-heritage',
  '/exotic-wildlife',
  '/exotic-wildlife-program',
  '/exotic-wildlife-association',
];
const heritageImporter: ChunkImporter = () =>
  import('@/components/heritage/HeritagePageRenderer');
for (const path of HERITAGE_SLUGS) {
  PREFETCH_IMPORTERS[path] = heritageImporter;
}

const warmedPaths = new Set<string>();
/**
 * Paths whose lazy chunk has fully resolved at least once. Distinct from
 * `warmedPaths`, which tracks *requested* prefetches (the import promise
 * may still be in-flight). `loadedPaths` only contains paths whose module
 * is sitting in the browser's module cache and will resolve synchronously
 * on the next `import()` call.
 */
const loadedPaths = new Set<string>();

export const markLoaded = (path: string) => {
  loadedPaths.add(path);
};

/**
 * Returns true if the lazy chunk for `pathname` has already been imported
 * to completion (idle prefetch, hover prefetch, or a prior visit). Used by
 * the route-fallback instrumentation to set the `cacheHit` flag — when
 * true we expect Suspense to never paint a fallback at all.
 */
export function isPathCached(pathname: string): boolean {
  if (loadedPaths.has(pathname)) return true;
  for (const p of loadedPaths) {
    if (pathname.startsWith(`${p}/`)) return true;
  }
  return false;
}

/**
 * Prefetch the lazy chunk for a known route on demand. Idempotent — each
 * path is requested at most once per page load. Safe to call from hover,
 * focus, or intersection-observer callbacks. Unknown paths are ignored.
 */
export function prefetchPath(path: string): void {
  if (typeof window === 'undefined') return;
  if (warmedPaths.has(path)) return;
  let importer = PREFETCH_IMPORTERS[path];
  // Dynamic gallery routes (`/galleries/:slug`) all live in the shared
  // `galleries` chunk — warm it via the GalleryPage importer.
  if (!importer && path.startsWith('/galleries/')) {
    importer = PREFETCH_IMPORTERS['/galleries'];
  }
  if (!importer) return;
  if (!shouldPrefetch()) return; // Slow/metered → defer until conditions improve.
  warmedPaths.add(path);
  enqueuePrefetch(() =>
    importer()
      .then(() => markLoaded(path))
      .catch(() => {
        // Allow a future retry if this attempt failed (e.g. offline).
        warmedPaths.delete(path);
      }),
  );
}

/** Returns true if the given pathname is one of the prefetch-warmed routes. */
export function isPrefetchedPath(pathname: string): boolean {
  // Exact match first; then prefix-with-slash match for nested routes (none today).
  if (PREFETCHED_PATHS.has(pathname)) return true;
  for (const p of PREFETCHED_PATHS) {
    if (pathname.startsWith(`${p}/`)) return true;
  }
  return false;
}

let scheduled = false;

const schedule = (cb: () => void) => {
  type IdleWindow = Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  };
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(cb, { timeout: 4000 });
  } else {
    setTimeout(cb, 1500);
  }
};

export function prefetchTopRoutes(): void {
  if (scheduled || typeof window === 'undefined') return;
  scheduled = true;

  schedule(() => {
    if (!shouldPrefetch()) return; // Mobile / Save-Data → skip warmup entirely.
    // Stagger imports so we don't fight the network with hero assets;
    // the concurrency limiter further caps fan-out at MAX_CONCURRENT.
    TOP_ROUTE_ENTRIES.forEach(({ importer, path }, i) => {
      setTimeout(() => {
        if (warmedPaths.has(path)) return;
        warmedPaths.add(path);
        enqueuePrefetch(() =>
          importer()
            .then(() => markLoaded(path))
            .catch(() => {
              warmedPaths.delete(path);
              /* swallow — will retry on actual navigation via lazyWithRetry */
            }),
        );
      }, i * 250);
    });
  });
}

/**
 * Re-warm any top routes that haven't yet been imported. Unlike
 * `prefetchTopRoutes`, this is safe to call multiple times — it skips paths
 * already in `warmedPaths` and is the right hook for auth-state transitions
 * (sign in / sign out / admin gate resolved) where the prior idle warmup may
 * have been cancelled by user navigation or never run on a deep-link entry.
 */
export function rewarmTopRoutes(): void {
  if (typeof window === 'undefined') return;
  schedule(() => {
    if (!shouldPrefetch()) return;
    TOP_ROUTE_ENTRIES.forEach(({ path }, i) => {
      if (warmedPaths.has(path)) return;
      setTimeout(() => prefetchPath(path), i * 250);
    });
  });
}
